import { Button, Skeleton } from '@teach/ui'
import { formatCreditsWithWord } from '@/features/ai/lib/money'
import { PaymentStroke } from './PaymentStroke'
import { useGetCreditsBalanceQuery } from '@/features/ai/creditsApi'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  /** Закрыть диалог: единственное осмысленное действие после успеха. */
  onDone: () => void
}

const AMOUNT = '-mt-2 text-3xl font-bold leading-none'

/**
 * Сколько пришло. Крупно — купленная сумма (`lastPurchaseAmount` приезжает в
 * том же ответе баланса, который перечитывается по подтверждению платежа), под
 * ней мелко — общий баланс. Старый бэк поля не отдаёт — тогда крупно баланс,
 * как раньше.
 *
 * Пока баланс ПЕРЕчитывается (`isFetching`) — скелет: в кеше ещё прошлый
 * ответ, и «+N» показал бы сумму ПРЕДЫДУЩЕЙ покупки.
 */
const ToppedUpAmount = () => {
  const { data, isError, isFetching } = useGetCreditsBalanceQuery()
  const m = useMessages(messages)
  // Прочерк, а не вечный скелет: оплата прошла, а перечитать баланс не
  // удалось — крутилка рядом с «Баланс пополнен» обещала бы, что число
  // вот-вот приедет.
  if (isError || (data !== undefined && !isFetching && !data.balance)) {
    return <p className={AMOUNT}>—</p>
  }
  if (!data?.balance || isFetching) return <Skeleton className="-mt-2 h-9 w-40" />
  if (!data.lastPurchaseAmount)
    return <p className={AMOUNT}>{formatCreditsWithWord(data.balance)}</p>
  return (
    <>
      <p className={AMOUNT}>+{formatCreditsWithWord(data.lastPurchaseAmount)}</p>
      <p className="-mt-1 text-sm text-muted-foreground">
        {m.balanceNow(formatCreditsWithWord(data.balance))}
      </p>
    </>
  )
}

/**
 * Четвёртый шаг мастера. Отдельным экраном, а не строкой внутри ожидания:
 * автор должен увидеть, что покупка кончилась, а не догадываться об этом по
 * исчезнувшей крутилке.
 */
export const PaymentDone = ({ onDone }: Props) => {
  const m = useMessages(messages)
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <PaymentStroke />
      {/* Подпись и сумма — одна смысловая пара: зазор между ними вдвое меньше
          общего (-mt-2 у суммы), чтобы они не читались двумя отдельными блоками. */}
      <p className="text-xl font-semibold leading-none text-success">{m.balanceToppedUpExcl}</p>
      <ToppedUpAmount />
      <Button size="xl" className="mt-1.5" onClick={onDone}>
        {m.backToWork}
      </Button>
    </div>
  )
}
